import React, { useEffect, useRef, useState } from 'react';
import { FiArrowRight, FiInstagram, FiMail, FiPhone } from 'react-icons/fi';
import { sendInquiryEmail, isEmailJsConfigured, getEmailJsConfigError } from '../services/emailService';

const testimonials = [
  {
    quote: 'Deeply impressed by the work and even more by the team. Yogesh’s humble and thoughtful approach made the journey to our new home a pleasure. We are absolutely delighted with the results.',
    author: 'Murali Ram, Triplicane',
  },
  {
    quote: 'Exceeding expectations in every way. The team delivered a premium design outcome that perfectly respected our budget. I am delighted with the results.',
    author: 'Arvind, Vandalur',
  },
];

export default function FooterContact() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [details, setDetails] = useState('');
  const [feedback, setFeedback] = useState('');
  const [sending, setSending] = useState(false);
  const [hoverDetail, setHoverDetail] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const toastTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  const showToast = (message) => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setToastMessage(message);
    toastTimeoutRef.current = setTimeout(() => {
      setToastMessage('');
      toastTimeoutRef.current = null;
    }, 3000);
  };

  const contactMethods = [
    {
      id: 'insta',
      label: 'Instagram',
      detail: '@the.art.and.lore.studio',
      Icon: FiInstagram,
      action: () => window.open('https://instagram.com/the.art.and.lore.studio', '_blank'),
    },
    {
      id: 'phone',
      label: 'Phone',
      detail: '8807140897',
      Icon: FiPhone,
      action: async () => {
        try {
          await navigator.clipboard.writeText('8807140897');
          showToast('Copied phone number to clipboard.');
        } catch (err) {
          showToast('Unable to copy the phone number.');
        }
      },
    },
    {
      id: 'email',
      label: 'Email',
      detail: 'theartandlorestudio@gmail.com',
      Icon: FiMail,
      action: async () => {
        try {
          await navigator.clipboard.writeText('theartandlorestudio@gmail.com');
          showToast('Copied email address to clipboard.');
        } catch (err) {
          showToast('Unable to copy the email address.');
        }
      },
    },
  ];

  const handleContactClick = async (method) => {
    await method.action();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFeedback('');

    if (!isEmailJsConfigured()) {
      setFeedback(getEmailJsConfigError());
      return;
    }

    setSending(true);
    try {
      await sendInquiryEmail({ name, email, phone, details });
      setFeedback('Inquiry sent successfully. Thank you!');
      setName('');
      setEmail('');
      setPhone('');
      setDetails('');
    } catch (error) {
      console.error('EmailJS error:', error);
      setFeedback('Something went wrong while sending the inquiry. Please try again later.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="relative overflow-hidden bg-[#070707] py-24">
      <div className="absolute inset-y-0 left-0 hidden w-64 overflow-hidden md:block">
        <span className="absolute -left-10 top-16 text-[10rem] font-serif uppercase tracking-[0.75em] text-white/5">Lore</span>
      </div>
      <div className="absolute right-0 top-20 h-72 w-72 rounded-full bg-amber-300/10 blur-3xl" />

      <div className="relative mx-auto max-w-[1400px] px-6">
        <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="relative overflow-hidden rounded-[36px] border border-white/10 bg-white/5 p-10 shadow-[0_35px_110px_rgba(0,0,0,0.35)]">
            <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),transparent_60%)]" />
            <div className="relative z-10 space-y-10">
              <div>
                <p className="text-sm uppercase tracking-[0.4em] text-amber-200/90">Testimonials</p>
                <h2 className="mt-4 text-2xl leading-tight text-white">The human side of our design process. Real Client Feedbacks.</h2>
              </div>

              <div className="space-y-8">
                {testimonials.map((item, index) => (
                  <div key={index} className="relative overflow-hidden rounded-[28px] border border-white/10 bg-[#111111]/95 p-8 shadow-[0_20px_60px_rgba(0,0,0,0.3)]">
                    <div className="absolute -left-10 top-1/2 h-28 w-28 -translate-y-1/2 rounded-full border border-amber-300/20 bg-amber-300/10" />
                    <div className="relative z-10">
                      <p className="text-[4rem] font-serif leading-none text-amber-200/60">“</p>
                      <p className="mt-4 text-lg leading-8 text-gray-200">{item.quote}</p>
                      <p className="mt-6 text-sm uppercase tracking-[0.35em] text-amber-200">{item.author}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-8">
            <div className="relative overflow-hidden rounded-[36px] border border-white/10 bg-white/5 p-10 shadow-[0_35px_110px_rgba(0,0,0,0.35)]">
              <div className="absolute -right-10 top-10 h-40 w-40 rounded-full border border-amber-300/20 bg-amber-300/10 blur-3xl" />
              <div className="relative z-10 space-y-6">
                <p className="text-sm uppercase tracking-[0.35em] text-amber-200/90">Connect</p>
                <h3 className="text-4xl font-semibold text-white">Begin the next chapter of your space.</h3>
                <p className="text-gray-300">
                  Share your project vision and we’ll guide you through a refined design process with clarity, craft, and quiet confidence.
                </p>
                <div className="mt-8 rounded-[32px] border border-white/10 bg-[#111111]/85 p-6">
                  <div className="grid gap-4 sm:grid-cols-3">
                    {contactMethods.map((method) => {
                      const Icon = method.Icon;
                      return (
                        <button
                          key={method.id}
                          type="button"
                          onClick={() => handleContactClick(method)}
                          onMouseEnter={() => setHoverDetail(method.detail)}
                          onFocus={() => setHoverDetail(method.detail)}
                          onMouseLeave={() => setHoverDetail('')}
                          onBlur={() => setHoverDetail('')}
                          className="flex h-20 flex-col items-center justify-center rounded-[24px] border border-white/10 bg-[#111111]/95 text-white transition hover:border-amber-300/50 hover:bg-white/5"
                        >
                          <Icon className="h-6 w-6" />
                          <span className="mt-2 text-xs uppercase tracking-[0.25em] text-gray-300">{method.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-4 min-h-[1.25rem] text-sm text-gray-300">
                    {hoverDetail || 'Hover an icon to preview details. Click to act.'}
                  </p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="grid gap-5 rounded-[36px] border border-white/10 bg-[#111111]/95 p-8 shadow-[0_35px_110px_rgba(0,0,0,0.35)]">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Name"
                className="rounded-[28px] border border-white/10 bg-transparent px-6 py-4 text-white outline-none transition focus:border-amber-300/80"
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Email"
                className="rounded-[28px] border border-white/10 bg-transparent px-6 py-4 text-white outline-none transition focus:border-amber-300/80"
              />
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone"
                className="rounded-[28px] border border-white/10 bg-transparent px-6 py-4 text-white outline-none transition focus:border-amber-300/80"
              />
              <textarea
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                required
                placeholder="Project details"
                className="h-36 rounded-[28px] border border-white/10 bg-transparent px-6 py-4 text-white outline-none transition focus:border-amber-300/80"
              />
              <button
                type="submit"
                disabled={sending}
                className="inline-flex items-center justify-center rounded-full bg-amber-200 px-8 py-4 text-sm font-semibold uppercase tracking-[0.25em] text-[#111] transition hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sending ? 'Sending...' : 'Send inquiry'}
                <FiArrowRight className="ml-3" />
              </button>
              {feedback && (
                <p className="mt-3 text-sm text-amber-200">{feedback}</p>
              )}
            </form>
          </div>
        </div>
      </div>
      {toastMessage && (
        <div className="pointer-events-none fixed bottom-8 right-8 z-50 rounded-[28px] border border-white/10 bg-slate-950/95 px-5 py-4 text-sm text-amber-100 shadow-[0_20px_60px_rgba(0,0,0,0.35)] backdrop-blur-xl">
          {toastMessage}
        </div>
      )}
    </section>
  );
}
